/**
 * Page compte client e-commerce
 * Profil, historique commandes, adresses
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../utils/pos.utils';
import { useCustomer } from '../contexts/CustomerContext';
import { logger } from '../utils/logger';

interface Order {
  id: string;
  order_number: string;
  created_at: string;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'delivered' | 'cancelled';
  total_cents: number;
  items_count: number;
  delivery_method: string;
  payment_method: string;
}

interface CustomerProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  created_at: string;
}

interface Preorder {
  id: string;
  preorder_number: string;
  customer_name: string;
  pickup_date: string;
  status: 'PENDING' | 'CONFIRMED' | 'READY' | 'PICKED_UP' | 'CANCELLED';
  source: 'POS' | 'ECOMMERCE';
  notes?: string;
  created_at: string;
  items: Array<{
    id: string;
    product_id: string;
    quantity: number;
    product: {
      name: string;
    };
  }>;
}

type TabType = 'profile' | 'orders' | 'preorders' | 'addresses';

export default function CustomerAccount() {
  const navigate = useNavigate();
  const { customer, selectedTenantId, isLoading: customerLoading } = useCustomer();
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [preorders, setPreorders] = useState<Preorder[]>([]);
  const [editing, setEditing] = useState(false);

  // Formulaire édition profil
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    loadProfile();
    if (activeTab === 'orders') {
      loadOrders();
    } else if (activeTab === 'preorders' && customer && !customerLoading) {
      loadPreorders();
    }
  }, [activeTab, customer, customerLoading]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/customer/profile');
      if (!response.ok) {
        // Si pas authentifié, rediriger vers login
        if (response.status === 401) {
          alert('Veuillez vous connecter');
          navigate('/login');
          return;
        }
        throw new Error('Erreur chargement profil');
      }
      const data = await response.json();
      setProfile(data);
      setFormData({
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        email: data.email,
      });
    } catch (error) {
      logger.error('Erreur chargement profil:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/customer/orders');
      if (!response.ok) throw new Error('Erreur chargement commandes');
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      logger.error('Erreur chargement commandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPreorders = async () => {
    try {
      setLoading(true);

      // Vérifier que le customer est connecté
      if (!customer || !selectedTenantId) {
        logger.warn('[CustomerAccount] Customer ou tenant non disponible');
        setPreorders([]);
        return;
      }

      logger.log('[CustomerAccount] Chargement précommandes:', {
        customerId: customer.id,
        tenantId: selectedTenantId,
      });

      const response = await fetch('/api/preorders/customer/my-preorders', {
        headers: {
          'X-Tenant-ID': selectedTenantId,
          'X-Customer-ID': customer.id,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[CustomerAccount] Erreur API précommandes:', response.status, errorText);
        throw new Error('Erreur chargement précommandes');
      }

      const data = await response.json();
      logger.log('[CustomerAccount] Précommandes chargées:', data.length);
      setPreorders(data);
    } catch (error) {
      logger.error('[CustomerAccount] Erreur chargement précommandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/customer/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Erreur sauvegarde profil');
      const updated = await response.json();
      setProfile(updated);
      setEditing(false);
      alert('✅ Profil mis à jour avec succès');
    } catch (error) {
      logger.error('Erreur sauvegarde profil:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status: Order['status']): string => {
    const labels: Record<Order['status'], string> = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      preparing: 'En préparation',
      ready: 'Prête',
      delivered: 'Livrée',
      cancelled: 'Annulée',
    };
    return labels[status];
  };

  const getStatusColor = (status: Order['status']): string => {
    const colors: Record<Order['status'], string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      preparing: 'bg-purple-100 text-purple-800',
      ready: 'bg-green-100 text-green-800',
      delivered: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Mon compte</h1>
            <button
              onClick={() => navigate('/shop')}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Retour à la boutique
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <nav className="flex flex-col">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`px-6 py-4 text-left font-medium border-l-4 ${
                    activeTab === 'profile'
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-transparent text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Profil
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`px-6 py-4 text-left font-medium border-l-4 ${
                    activeTab === 'orders'
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-transparent text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                      />
                    </svg>
                    Mes commandes
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('preorders')}
                  className={`px-6 py-4 text-left font-medium border-l-4 ${
                    activeTab === 'preorders'
                      ? 'border-purple-600 bg-purple-50 text-purple-900'
                      : 'border-transparent text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📦</span>
                    Mes précommandes
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('addresses')}
                  className={`px-6 py-4 text-left font-medium border-l-4 ${
                    activeTab === 'addresses'
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-transparent text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Adresses
                  </div>
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Mon profil</h2>
                    {!editing && (
                      <button
                        onClick={() => setEditing(true)}
                        className="px-4 py-2 text-blue-600 hover:text-blue-800"
                      >
                        Modifier
                      </button>
                    )}
                  </div>

                  {loading && !profile ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  ) : editing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Prénom
                          </label>
                          <input
                            type="text"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom
                          </label>
                          <input
                            type="text"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Téléphone
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="flex gap-2 pt-4">
                        <button
                          onClick={handleSaveProfile}
                          disabled={loading}
                          className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
                        >
                          Enregistrer
                        </button>
                        <button
                          onClick={() => {
                            setEditing(false);
                            if (profile) {
                              setFormData({
                                first_name: profile.first_name,
                                last_name: profile.last_name,
                                phone: profile.phone,
                                email: profile.email,
                              });
                            }
                          }}
                          className="px-6 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  ) : profile ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Prénom
                          </label>
                          <p className="text-gray-900">{profile.first_name}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Nom
                          </label>
                          <p className="text-gray-900">{profile.last_name}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Email
                          </label>
                          <p className="text-gray-900">{profile.email}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Téléphone
                          </label>
                          <p className="text-gray-900">{profile.phone}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-500 mb-1">
                            Membre depuis
                          </label>
                          <p className="text-gray-900">
                            {new Date(profile.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Orders Tab */}
              {activeTab === 'orders' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Mes commandes</h2>

                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                        />
                      </svg>
                      <p className="mt-4 text-gray-500">Aucune commande pour le moment</p>
                      <button
                        onClick={() => navigate('/shop')}
                        className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Découvrir nos produits
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders.map((order) => (
                        <div
                          key={order.id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Commande #{order.order_number}
                                </h3>
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                    order.status
                                  )}`}
                                >
                                  {getStatusLabel(order.status)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-500">
                                {new Date(order.created_at).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                })}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {order.items_count} article{order.items_count > 1 ? 's' : ''}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-600">
                                {formatPrice(order.total_cents)}
                              </p>
                              <button className="mt-2 text-blue-600 hover:text-blue-800 text-sm">
                                Voir détails
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Preorders Tab */}
              {activeTab === 'preorders' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Mes précommandes</h2>

                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full"></div>
                    </div>
                  ) : preorders.length === 0 ? (
                    <div className="text-center py-12">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                        />
                      </svg>
                      <p className="mt-4 text-gray-500">Aucune précommande pour le moment</p>
                      <button
                        onClick={() => navigate('/shop')}
                        className="mt-4 px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        Découvrir nos produits
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {preorders.map((preorder) => (
                        <div
                          key={preorder.id}
                          className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-purple-50/30"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  📦 Précommande #{preorder.preorder_number}
                                </h3>
                                <span
                                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                    preorder.status === 'PENDING'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : preorder.status === 'CONFIRMED'
                                      ? 'bg-blue-100 text-blue-800'
                                      : preorder.status === 'READY'
                                      ? 'bg-green-100 text-green-800'
                                      : preorder.status === 'PICKED_UP'
                                      ? 'bg-gray-100 text-gray-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {preorder.status === 'PENDING'
                                    ? 'En attente'
                                    : preorder.status === 'CONFIRMED'
                                    ? 'Confirmé'
                                    : preorder.status === 'READY'
                                    ? 'Prêt'
                                    : preorder.status === 'PICKED_UP'
                                    ? 'Récupéré'
                                    : 'Annulé'}
                                </span>
                                {preorder.source && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                    {preorder.source === 'POS' ? 'En magasin' : 'En ligne'}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-2">
                                <strong>Retrait prévu :</strong>{' '}
                                {new Date(preorder.pickup_date).toLocaleDateString('fr-FR', {
                                  weekday: 'long',
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                })}
                              </p>
                              <p className="text-sm text-gray-500 mb-3">
                                Commandé le{' '}
                                {new Date(preorder.created_at).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                })}
                              </p>

                              {/* Liste des articles */}
                              <div className="mt-3 space-y-1">
                                <p className="text-sm font-medium text-gray-700">Articles :</p>
                                {preorder.items.map((item) => (
                                  <div
                                    key={item.id}
                                    className="text-sm text-gray-600 pl-3 flex items-center gap-2"
                                  >
                                    <span className="text-purple-600">•</span>
                                    <span>
                                      {item.quantity}x {item.product.name}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {preorder.notes && (
                                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                  <p className="text-sm text-gray-700">
                                    <strong>Note :</strong> {preorder.notes}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Addresses Tab */}
              {activeTab === 'addresses' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Mes adresses</h2>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                      Ajouter une adresse
                    </button>
                  </div>

                  <div className="text-center py-12">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <p className="mt-4 text-gray-500">Aucune adresse enregistrée</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
